import React, { useState, useEffect } from 'react'
import { Upload, message, Typography } from 'antd'
import { InboxOutlined } from '@ant-design/icons'
import { Controller, useFormState } from 'react-hook-form'

import styles from './UploadField.module.css'

const { Dragger } = Upload
const { Text } = Typography

const MAX_FILES = 1
const MAX_SIZE_MB = 70 // Changez cette valeur selon vos besoins
const MAX_SIZE = MAX_SIZE_MB * 1024 * 1024

const isValidFile = (file) => {
  const validTypes = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/jpeg',
    'image/png',
    'image/gif',
    'video/mp4',
    'audio/mpeg',
    'audio/wav',
  ]
  return validTypes.includes(file.type)
}

const UploadRender = React.forwardRef(
  ({ onChange = () => {}, file, ...props }, ref) => {
    const [fileList, setFileList] = useState([])

    useEffect(() => {
      if (
        file &&
        typeof file === 'string' &&
        !['null', 'Null', '', 'undefined'].includes(file)
      ) {
        const existingFile = {
          uid: '-1',
          name: file,
          status: 'done',
        }
        setFileList([existingFile])
      }
    }, [file])

    const propsUpload = {
      name: 'file',
      multiple: false,
      accept: '.pdf,.xlsx,.jpg,.jpeg,.png,.gif,.mp4,.mp3,.wav',
      fileList: fileList,
      beforeUpload: (file) => {
        if (!isValidFile(file)) {
          message.error('Format de fichier non autorisé.')
          return false
        }

        if (fileList.length >= MAX_FILES) {
          message.error(
            `Vous ne pouvez pas télécharger plus de ${MAX_FILES} fichier.`
          )
          return false
        }

        const totalSize =
          fileList.reduce((acc, curr) => acc + curr.size, 0) + file.size
        if (totalSize > MAX_SIZE) {
          message.error(
            `La taille totale ne doit pas dépasser ${MAX_SIZE_MB}MB.`
          )
          return false
        }

        setFileList([file])
        onChange(file)
        return false
      },
      onRemove: (file) => {
        setFileList([])
      },
    }

    return (
      <>
        <Dragger {...propsUpload} {...props}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Cliquez ou faites glisser un fichier pour le télécharger
          </p>
          <Text type="secondary">
            Types de fichiers acceptés: PDF, XLSX, JPEG, PNG, GIF, MP4, MP3,
            WAV. Taille maximale: {MAX_SIZE_MB}MB.
          </Text>
        </Dragger>
      </>
    )
  }
)

const ActiveUploadField = ({ field, control }) => {
  const { errors, isSubmitted } = useFormState({ control })
  const input_style = field.input_design ? { ...field.input_design } : {}

  let containerClass = styles.container

  if (isSubmitted) {
    containerClass += errors[field.idFieldCanvas]
      ? ' input-error'
      : ' input-success'
  }

  return (
    <div className={containerClass}>
      <Controller
        control={control}
        rules={{ required: field.required }}
        name={field.idFieldCanvas}
        defaultValue={field.VALUE__reponse || null}
        render={({ field: { onChange, value } }) => (
          <UploadRender
            style={input_style}
            onChange={(files) => onChange(files)}
            file={value}
          />
        )}
      />
    </div>
  )
}

const InactiveUploadField = ({ field }) => {
  const input_style = field.input_design ? { ...field.input_design } : {}
  return (
    <div className={styles.container}>
      <UploadRender style={input_style} />
    </div>
  )
}

const UploadField = ({ field, control, isActiveSubmit }) => {
  return isActiveSubmit ? (
    <ActiveUploadField field={field} control={control} />
  ) : (
    <InactiveUploadField field={field} />
  )
}

export default UploadField
